import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../../firebase/config';
import { PaperclipIcon } from '../../components/icons/Icons';

// This is the modal for viewing and editing a submitted invoice.
export default function ViewInvoiceModal({ invoice, onClose, userProfile, appId }) {
    const formatDateForInput = (date) => {
        if (!date) return '';
        const d = date.toDate ? date.toDate() : new Date(date);
        return d.toISOString().split('T')[0];
    };

    const [amount, setAmount] = useState(invoice.amount || '');
    const [dueDate, setDueDate] = useState(formatDateForInput(invoice.dueDate));
    const [comments, setComments] = useState(invoice.comments || '');
    const [newInvoiceFile, setNewInvoiceFile] = useState(null);
    const [error, setError] = useState('');

    // An invoice is only editable by the original submitter, and only if it has been rejected.
    const isEditable = (invoice.status === 'Rejected' || invoice.status === 'Rejected by Finance') && invoice.submittedById === userProfile.uid;

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!amount || !dueDate) {
            setError('Amount and Due Date are required.');
            return;
        }
        setError('');

        const invoiceDocRef = doc(db, `/artifacts/${appId}/public/data/purchaseRequests/${invoice.purchaseRequestId}/invoices`, invoice.id);
        
        try {
            let updatedInvoiceFile = invoice.invoiceFile;

            if (newInvoiceFile) {
                // Delete the old file from storage if it exists
                if (invoice.invoiceFile && invoice.invoiceFile.path) {
                    const oldFileRef = ref(storage, invoice.invoiceFile.path);
                    await deleteObject(oldFileRef).catch(err => console.error("Could not delete old invoice file:", err));
                }
                // Upload the new file
                const newFilePath = `attachments/invoices/${userProfile.uid}/${Date.now()}-${newInvoiceFile.name}`;
                const newFileRef = ref(storage, newFilePath);
                // --- THIS LINE IS CORRECTED ---
                await uploadBytes(newFileRef, newInvoiceFile); 
                const newDownloadURL = await getDownloadURL(newFileRef);
                updatedInvoiceFile = { name: newInvoiceFile.name, url: newDownloadURL, path: newFilePath };
            }

            const updatePayload = {
                amount: parseFloat(amount),
                dueDate: new Date(dueDate),
                comments: comments,
                invoiceFile: updatedInvoiceFile,
                status: "Pending Invoice Approval", // Resubmit for approval
                updatedAt: serverTimestamp()
            };

            await updateDoc(invoiceDocRef, updatePayload);
            onClose(); // Close the modal on success
        } catch(e) {
            console.error("Error updating invoice:", e);
            alert("Failed to update invoice.");
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl p-8 w-full max-w-lg max-h-full overflow-y-auto">
                <h2 className="text-2xl font-semibold text-[#213368] mb-2">Invoice Details</h2>
                <p className="text-sm font-mono text-gray-500 mb-4">{invoice.invoiceNumber}</p>
                
                {isEditable && (
                    <div className="p-3 mb-4 bg-yellow-100 text-yellow-800 text-sm rounded-md">
                        This invoice was rejected. You can edit the details below and resubmit for approval.
                        {invoice.approvalComments && <p className="mt-1 italic"><strong>Approver's Comments:</strong> {invoice.approvalComments}</p>}
                        {invoice.financeRejectionReason && <p className="mt-1 italic"><strong>Finance Comments:</strong> {invoice.financeRejectionReason}</p>}
                    </div>
                )}

                <form onSubmit={handleUpdate} className="space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Invoice Amount ({invoice.currency})</label>
                        <input 
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
                            disabled={!isEditable}
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Due Date</label>
                        <input 
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
                            disabled={!isEditable}
                        />
                    </div>
                     <div>
                        <label className="block text-sm font-medium text-gray-700">Your Comments</label>
                        <textarea
                            value={comments}
                            onChange={(e) => setComments(e.target.value)}
                            rows="3"
                            className="mt-1 block w-full p-2 border border-gray-300 rounded-md shadow-sm disabled:bg-gray-100"
                            placeholder="Optional comments for the approver..."
                            disabled={!isEditable}
                        ></textarea>
                    </div>

                    {/* --- FIX: Display read-only comments when NOT in edit mode --- */}
                    {/* This was the missing piece. We were using the 'comments' state for the editable textarea, 
                        but not displaying the original invoice.comments when just viewing. */}
                    {!isEditable && invoice.comments && (
                         <div>
                            <label className="block text-sm font-medium text-gray-700">Submitter's Comments</label>
                            <p className="mt-1 block w-full p-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-700">{invoice.comments}</p>
                        </div>
                    )} 

		    <div>
                        <label className="block text-sm font-medium text-gray-700">Invoice File</label>
                        <div className="mt-1">
                            {invoice.invoiceFile?.url ? (
                                <a href={invoice.invoiceFile.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{invoice.invoiceFile.name}</a>
                            ) : 'No file attached'}
                        </div>
                        {isEditable && (
                            <div className="mt-2">
                                <label className="block text-sm font-medium text-gray-700">Upload New File (Optional)</label>
                                <input 
                                    type="file"
                                    onChange={(e) => setNewInvoiceFile(e.target.files[0])}
                                    className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-[#20BEC6] file:bg-opacity-20 file:text-[#20BEC6] hover:file:bg-opacity-30"
                                    accept="image/*,application/pdf"
                                />
                                {newInvoiceFile && <p className="text-xs text-green-600 mt-1">New file selected: {newInvoiceFile.name}</p>}
                            </div>
                        )}
                     </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}
                    
                    <div className="flex justify-end space-x-4 pt-4 border-t">
                        <button type="button" onClick={onClose} className="px-6 py-2 bg-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-300">
                            Close
                        </button>
                        {isEditable && (
                            <button type="submit" className="px-6 py-2 bg-[#20BEC6] text-white font-medium rounded-lg shadow-md hover:bg-opacity-90">
                                Update & Resubmit
                            </button>
                        )}
                    </div>
                </form>
            </div>
        </div>
    );
}